/**
 * AIFactoryDashboard
 *
 * Operational dashboard for content managers showing production metrics,
 * quality control, pipeline status, and bot-driven content prioritization.
 *
 * Part of Story 74: AI Factory Dashboard
 */

import { useState, useEffect } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/use-toast';
import {
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle2,
  Activity,
  Clock,
  Layers,
  Target,
  Bot,
  RefreshCw,
  Play,
  Pause,
  Factory
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  FunnelChart,
  Funnel,
  LabelList
} from 'recharts';
import { BotAttentionSection, type KeywordEnrichmentStatus } from '@/components/shared/BotAttentionSection';
import type { FactoryDashboardData } from '@/services/factoryDashboardService';
import {
  fetchDashboard,
  queueProductsForGeneration,
  fetchKeywordEnrichmentStatus,
  triggerKeywordEnrichment
} from '@/services/factoryDashboardService';
import { useFactoryWebSocket, type QualityAlertEvent } from '@/hooks/useFactoryWebSocket';

const STAGE_COLORS: Record<string, string> = {
  draft: '#f97316',
  validated: '#3b82f6',
  published: '#22c55e'
};

export default function AIFactoryDashboard() {
  const { selectedSite } = useSite();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<FactoryDashboardData | null>(null);
  const [activeTab, setActiveTab] = useState('production');
  const [isQueuing, setIsQueuing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false); // Default to false to prevent connection storms
  const [keywordEnrichmentStatus, setKeywordEnrichmentStatus] = useState<KeywordEnrichmentStatus | undefined>();
  const [isEnriching, setIsEnriching] = useState(false);

  // WebSocket for real-time updates
  const { metrics: liveMetrics, isConnected, lastUpdate } = useFactoryWebSocket(
    selectedSite?.id || null,
    {
      onQualityAlert: (alert: QualityAlertEvent) => {
        toast({
          title: alert.severity === 'critical' ? 'Quality Alert' : 'Quality Warning',
          description: `${alert.message} (${alert.affectedProducts} products affected)`,
          variant: alert.severity === 'critical' ? 'destructive' : 'default'
        });
      },
      onBatchCompleted: (event) => {
        toast({
          title: 'Batch Completed',
          description: `${event.state} batch completed: ${event.completedProducts}/${event.totalProducts} products processed.`
        });
        loadData(); // Refresh full data on batch completion
      },
      autoConnect: autoRefresh
    }
  );

  useEffect(() => {
    if (selectedSite) {
      loadData();
    }
  }, [selectedSite]);

  // Update production metrics from WebSocket when available
  useEffect(() => {
    if (liveMetrics && data) {
      setData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          production: {
            ...prev.production,
            currentBatch: liveMetrics.currentBatch ? {
              id: liveMetrics.currentBatch.id,
              status: liveMetrics.currentBatch.state,
              progress: liveMetrics.currentBatch.progress,
              totalProducts: liveMetrics.currentBatch.totalProducts,
              completedProducts: liveMetrics.currentBatch.completedProducts,
              startedAt: liveMetrics.currentBatch.startedAt
            } : null
          }
        };
      });
    }
  }, [liveMetrics]);

  const loadData = async () => {
    if (!selectedSite) return;
    setLoading(true);
    try {
      const [dashboardData, kwStatus] = await Promise.all([
        fetchDashboard(selectedSite.id),
        fetchKeywordEnrichmentStatus(selectedSite.id).catch(() => undefined)
      ]);
      setData(dashboardData);
      setKeywordEnrichmentStatus(kwStatus);
    } catch (error) {
      console.error('Failed to load factory dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQueueProducts = async (productIds: string[]) => {
    if (!selectedSite) return;
    setIsQueuing(true);
    try {
      const result = await queueProductsForGeneration(selectedSite.id, productIds);
      toast({
        title: 'Products Queued',
        description: `${result.queued} products queued for generation. ${result.alreadyQueued} were already queued.`
      });
      loadData(); // Refresh data
    } catch (error) {
      toast({
        title: 'Queue Failed',
        description: 'Failed to queue products for generation.',
        variant: 'destructive'
      });
    } finally {
      setIsQueuing(false);
    }
  };

  const handleKeywordEnrichment = async () => {
    if (!selectedSite) return;
    setIsEnriching(true);
    try {
      const result = await triggerKeywordEnrichment(selectedSite.id);
      toast({
        title: 'Keyword Enrichment Started',
        description: result.note
      });
      // Poll for status updates
      setTimeout(async () => {
        try {
          const status = await fetchKeywordEnrichmentStatus(selectedSite.id);
          setKeywordEnrichmentStatus(status);
        } catch {
          // Ignore polling errors
        }
        setIsEnriching(false);
      }, 5000);
    } catch (error) {
      toast({
        title: 'Enrichment Failed',
        description: 'Failed to start keyword enrichment.',
        variant: 'destructive'
      });
      setIsEnriching(false);
    }
  };

  const formatTrend = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value}%`;
  };

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please select a site to view the AI Factory dashboard.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load dashboard data. Please try again.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const { production, qualityControl, pipeline, impact, visibility, research, timeToValue, botAttentionPriority, cumulative } = data;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Factory className="h-6 w-6" />
            AI Factory Dashboard
          </h1>
          <p className="text-muted-foreground">
            Production operations for {data.meta.siteName}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {/* WebSocket Status */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground mr-2">
            <div className={`h-2 w-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-gray-400'}`} />
            {isConnected ? 'Live' : 'Offline'}
            {lastUpdate && (
              <span className="text-xs">
                (Updated: {lastUpdate.toLocaleTimeString()})
              </span>
            )}
          </div>
          <Button
            variant={autoRefresh ? 'default' : 'outline'}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? <Pause className="h-4 w-4 mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            {autoRefresh ? 'Live' : 'Paused'}
          </Button>
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Live Counters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Products Enriched (This Week)</p>
                <p className="text-3xl font-bold">{production.thisWeek.productsEnriched.toLocaleString()}</p>
              </div>
              <div className={`flex items-center ${production.trends.productsEnrichedChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {production.trends.productsEnrichedChange >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                <span className="text-sm ml-1">{formatTrend(production.trends.productsEnrichedChange)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pages Generated</p>
                <p className="text-3xl font-bold">{production.thisWeek.pagesGenerated.toLocaleString()}</p>
              </div>
              <div className={`flex items-center ${production.trends.pagesGeneratedChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {production.trends.pagesGeneratedChange >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                <span className="text-sm ml-1">{formatTrend(production.trends.pagesGeneratedChange)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Quality Pass Rate</p>
                <p className="text-3xl font-bold">{qualityControl.overallPassRate}%</p>
              </div>
              <CheckCircle2 className={`h-8 w-8 ${qualityControl.overallPassRate >= 90 ? 'text-green-500' : qualityControl.overallPassRate >= 75 ? 'text-yellow-500' : 'text-red-500'}`} />
            </div>
            <Progress value={qualityControl.overallPassRate} className="mt-3" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Specs Researched</p>
                <p className="text-3xl font-bold">{production.thisWeek.specsResearched.toLocaleString()}</p>
              </div>
              <div className={`flex items-center ${production.trends.specsResearchedChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {production.trends.specsResearchedChange >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                <span className="text-sm ml-1">{formatTrend(production.trends.specsResearchedChange)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Current Batch Status */}
      {production.currentBatch && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="h-5 w-5 animate-pulse text-green-500" />
              Active Batch: {production.currentBatch.status}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Progress value={production.currentBatch.progress} className="flex-1" />
              <span className="text-sm font-mono">
                {production.currentBatch.completedProducts}/{production.currentBatch.totalProducts}
                ({production.currentBatch.progress}%)
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-8">
          <TabsTrigger value="production">Production</TabsTrigger>
          <TabsTrigger value="quality">Quality</TabsTrigger>
          <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="impact">Impact</TabsTrigger>
          <TabsTrigger value="visibility">Visibility</TabsTrigger>
          <TabsTrigger value="research">Research</TabsTrigger>
          <TabsTrigger value="bot">Bot Priority</TabsTrigger>
          <TabsTrigger value="lifetime">Lifetime</TabsTrigger>
        </TabsList>

        {/* Production Tab */}
        <TabsContent value="production" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Daily Production Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Daily Production</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={production.dailyProduction}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="productsEnriched" name="Enriched" stackId="1" stroke="#3b82f6" fill="#3b82f6" />
                      <Area type="monotone" dataKey="pagesGenerated" name="Generated" stackId="2" stroke="#22c55e" fill="#22c55e" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Week over Week Comparison */}
            <Card>
              <CardHeader>
                <CardTitle>Week over Week</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        {
                          metric: 'Enriched',
                          'This Week': production.thisWeek.productsEnriched,
                          'Last Week': production.lastWeek.productsEnriched
                        },
                        {
                          metric: 'Generated',
                          'This Week': production.thisWeek.pagesGenerated,
                          'Last Week': production.lastWeek.pagesGenerated
                        },
                        {
                          metric: 'Researched',
                          'This Week': production.thisWeek.specsResearched,
                          'Last Week': production.lastWeek.specsResearched
                        }
                      ]}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="metric" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="Last Week" fill="#94a3b8" />
                      <Bar dataKey="This Week" fill="#3b82f6" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Quality Tab */}
        <TabsContent value="quality" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-green-50 dark:bg-green-950/20">
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Passed</p>
                <p className="text-3xl font-bold text-green-600">{qualityControl.thisMonth.passed.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50 dark:bg-yellow-950/20">
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Flagged</p>
                <p className="text-3xl font-bold text-yellow-600">{qualityControl.thisMonth.flagged.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 dark:bg-red-950/20">
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Blocked</p>
                <p className="text-3xl font-bold text-red-600">{qualityControl.thisMonth.blocked.toLocaleString()}</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Quality Trend */}
            <Card>
              <CardHeader>
                <CardTitle>Quality Trend (30d)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={qualityControl.qualityTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis domain={[0, 100]} />
                      <Tooltip />
                      <Line type="monotone" dataKey="passRate" name="Pass Rate %" stroke="#22c55e" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Top Rejection Reasons */}
            <Card>
              <CardHeader>
                <CardTitle>Top Rejection Reasons</CardTitle>
              </CardHeader>
              <CardContent>
                {qualityControl.topRejectionReasons.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No rejections to display
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Reason</TableHead>
                        <TableHead className="text-right">Count</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {qualityControl.topRejectionReasons.map((reason, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{reason.reason}</TableCell>
                          <TableCell className="text-right font-mono">{reason.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline" className="space-y-6">
          {/* Pipeline Stages */}
          <Card>
            <CardHeader>
              <CardTitle>Pipeline Funnel</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 items-center justify-center">
                {pipeline.stages.map((stage, idx) => (
                  <div key={stage.stage} className="flex items-center">
                    <div
                      className="px-6 py-4 rounded-lg text-center"
                      style={{ backgroundColor: STAGE_COLORS[stage.stage] || '#6b7280' }}
                    >
                      <p className="text-white text-2xl font-bold">{stage.count}</p>
                      <p className="text-white/80 text-sm capitalize">{stage.stage}</p>
                    </div>
                    {idx < pipeline.stages.length - 1 && (
                      <div className="text-muted-foreground px-2">→</div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Bottleneck Alert */}
          {pipeline.bottleneck && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Bottleneck detected at <strong>{pipeline.bottleneck.stage}</strong>:
                {pipeline.bottleneck.queueSize} items waiting (avg {pipeline.bottleneck.avgWaitTime}h)
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Stage Timing */}
            <Card>
              <CardHeader>
                <CardTitle>Average Time per Stage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {pipeline.avgTimePerStage.map(stage => (
                    <div key={stage.stage} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="capitalize">{stage.stage}</span>
                        <span className={stage.avgHours > stage.targetHours ? 'text-red-600' : 'text-green-600'}>
                          {stage.avgHours}h / {stage.targetHours}h target
                        </span>
                      </div>
                      <Progress value={Math.min((stage.avgHours / stage.targetHours) * 100, 150)} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Stuck Items */}
            <Card>
              <CardHeader>
                <CardTitle>Stuck Items ({'>'}7 days)</CardTitle>
              </CardHeader>
              <CardContent>
                {pipeline.stuckItems.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
                    No stuck items
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Stage</TableHead>
                        <TableHead className="text-right">Count</TableHead>
                        <TableHead className="text-right">Oldest</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pipeline.stuckItems.map(item => (
                        <TableRow key={item.stage}>
                          <TableCell className="capitalize">{item.stage}</TableCell>
                          <TableCell className="text-right font-mono">{item.count}</TableCell>
                          <TableCell className="text-right">{item.oldestDays}d</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Impact Tab */}
        <TabsContent value="impact" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Before vs After Enrichment</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {impact.metrics.map(metric => (
                  <div key={metric.metric} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{metric.metric}</span>
                      <Badge variant={metric.improvement > 0 ? 'default' : 'secondary'}>
                        {metric.improvement > 0 ? '+' : ''}{metric.improvement}{metric.unit}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Before</p>
                        <p className="text-2xl font-bold text-gray-500">{metric.before}{metric.unit}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">After</p>
                        <p className="text-2xl font-bold text-green-600">{metric.after}{metric.unit}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Visibility Tab */}
        <TabsContent value="visibility" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Discovery Rate</p>
                <p className="text-3xl font-bold">{visibility.discoveryRate}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Avg Time to First Crawl</p>
                <p className="text-3xl font-bold">{visibility.avgTimeToFirstCrawl}h</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Traffic Lift</p>
                <p className="text-3xl font-bold text-green-600">+{visibility.trafficLift.liftPercent}%</p>
              </CardContent>
            </Card>
          </div>

          {/* Bot Discovery */}
          <Card>
            <CardHeader>
              <CardTitle>Bot Discovery (30d)</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bot</TableHead>
                    <TableHead className="text-right">Visits</TableHead>
                    <TableHead className="text-right">Pages Visited</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibility.botDiscovery.map(bot => (
                    <TableRow key={bot.botType}>
                      <TableCell className="font-medium">{bot.botType}</TableCell>
                      <TableCell className="text-right font-mono">{bot.visitsLast30d.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono">{bot.pagesVisited.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Research Tab */}
        <TabsContent value="research" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Success Rate</p>
                <p className="text-3xl font-bold">{research.successRate}%</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 dark:bg-green-950/20">
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Values Found</p>
                <p className="text-3xl font-bold text-green-600">{research.outcomes.found.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="bg-gray-50 dark:bg-gray-950/20">
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Not Available</p>
                <p className="text-3xl font-bold text-gray-600">{research.outcomes.notAvailable.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 dark:bg-blue-950/20">
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Still Searching</p>
                <p className="text-3xl font-bold text-blue-600">{research.outcomes.searching.toLocaleString()}</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Bot Priority Tab */}
        <TabsContent value="bot">
          <BotAttentionSection
            priorityItems={botAttentionPriority.priorityItems}
            summary={botAttentionPriority.summary}
            trendingCategories={botAttentionPriority.trendingCategories}
            aiAssistantFocus={botAttentionPriority.aiAssistantFocus}
            discoveryProblems={botAttentionPriority.discoveryProblems}
            variant="factory"
            onQueueForGeneration={handleQueueProducts}
            isQueuing={isQueuing}
            keywordEnrichmentStatus={keywordEnrichmentStatus}
            onTriggerKeywordEnrichment={handleKeywordEnrichment}
            isEnriching={isEnriching}
          />
        </TabsContent>

        {/* Lifetime Tab */}
        <TabsContent value="lifetime" className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Products Enriched</p>
                <p className="text-2xl font-bold">{cumulative.lifetime.productsEnriched.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Pages Created</p>
                <p className="text-2xl font-bold">{cumulative.lifetime.pagesCreated.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Specs Added</p>
                <p className="text-2xl font-bold">{cumulative.lifetime.specsAdded.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Pages Indexed</p>
                <p className="text-2xl font-bold">{cumulative.lifetime.pagesIndexed.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Additional Visits</p>
                <p className="text-2xl font-bold">{cumulative.lifetime.additionalVisits.toLocaleString()}</p>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Growth */}
          <Card>
            <CardHeader>
              <CardTitle>Monthly Growth</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={cumulative.monthlyGrowth}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="productsEnriched" name="Products" fill="#3b82f6" />
                    <Bar dataKey="pagesCreated" name="Pages" fill="#22c55e" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Milestones */}
          {cumulative.milestones.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Milestones</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {cumulative.milestones.map((milestone, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <span>{milestone.title}</span>
                      <span className="text-muted-foreground text-sm">- {milestone.achievedAt}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
